// ===========================================
// Public page shell — the default public template
// ===========================================
// The frame every public, unauthenticated page shares: cream background,
// character and music-note decorations, the piano header with the logo and the
// page title, and the footer. Extracted 2026-10-09 from the enquiry and Meet &
// Greet pages, which carried byte-identical copies of it, so the permission-slip
// signing page could use it too.
//
// ⚠️ This is the Music 'n Me look, HARD-CODED on purpose (Andrew, 2026-10-09):
// Music 'n Me is the only school, so its look is the default template. Letting a
// school choose its own profile template is a separate future feature; that is
// where per-school logos and colours belong, not in this component.

import type { ReactNode } from 'react';
import { Box, Container, Typography } from '@mui/material';
import PublicFooter from './PublicFooter';

const HEADING_FONT = '"Monkey Mayhem", "Comic Sans MS", cursive';

interface PublicPageShellProps {
  /** Shown over the piano header (desktop) and under the logo (mobile). */
  title: string;
  /** Optional line under the header. */
  subtitle?: ReactNode;
  /** Passed to the footer; it falls back to "Music 'n Me". */
  schoolName?: string;
  schoolSlug?: string;
  children: ReactNode;
}

export default function PublicPageShell({
  title,
  subtitle,
  schoolName,
  schoolSlug,
  children,
}: PublicPageShellProps) {
  return (
    <Box
      sx={{
        bgcolor: '#FCF6E6', // Cream brand color
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Character Decorations - Left */}
      <Box
        component="img"
        src="/character.png"
        alt=""
        sx={{
          position: 'fixed',
          left: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: 'calc(10% - 50px)', md: 'calc(20% - 50px)' },
          width: { xs: 120, md: 160, lg: 200 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(-10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Character Decorations - Right */}
      <Box
        component="img"
        src="/character-mint.png"
        alt=""
        sx={{
          position: 'fixed',
          right: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: '30%', md: '40%' },
          width: { xs: 100, md: 140, lg: 180 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Music Notes Decorations */}
      <Box
        sx={{
          position: 'fixed',
          left: { xs: 10, md: 40 },
          top: 'calc(30% + 50px)',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%' }} />
      </Box>
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 10, md: 40 },
          top: '50%',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%' }} />
      </Box>

      {/* Mobile Header - Logo centered, no piano */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 2,
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            maxWidth: 120,
            height: 'auto',
            mb: 2,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: HEADING_FONT,
            color: 'primary.main',
            textAlign: 'center',
            fontSize: '1.75rem',
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Desktop Header - Piano with Logo Overlay */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'relative',
          width: '100%',
          mb: 3,
        }}
      >
        {/* Piano Background - full height */}
        <Box
          component="img"
          src="/piano-header.png"
          alt="Piano keys"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
        {/* Logo - top left */}
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            position: 'absolute',
            top: { sm: 12, md: 16 },
            left: { sm: 12, md: 16 },
            maxWidth: { sm: 100, md: 120 },
            height: 'auto',
          }}
        />
        {/* Title overlay - bottom */}
        <Typography
          variant="h3"
          sx={{
            position: 'absolute',
            bottom: 5,
            left: 0,
            right: 0,
            fontFamily: HEADING_FONT,
            color: 'white',
            textAlign: 'center',
            fontSize: { sm: '2rem', md: '2.5rem' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </Typography>
      </Box>

      {subtitle && (
        <Container maxWidth="md" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
          <Typography variant="h6" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        </Container>
      )}

      {children}

      <PublicFooter schoolName={schoolName} schoolSlug={schoolSlug} />
    </Box>
  );
}
